/**
 * 文件名处理工具函数
 */

// 音频文件扩展名映射
const AUDIO_MIME_TO_EXT: Record<string, string> = {
  'audio/mpeg': '.mp3',
  'audio/mp3': '.mp3',
  'audio/wav': '.wav',
  'audio/wave': '.wav',
  'audio/x-wav': '.wav',
  'audio/m4a': '.m4a',
  'audio/mp4': '.m4a',
  'audio/aac': '.aac',
  'audio/ogg': '.ogg',
  'audio/vorbis': '.ogg',
  'audio/flac': '.flac',
  'audio/x-flac': '.flac',
  'audio/webm': '.webm',
  'audio/3gpp': '.3gp',
  'audio/amr': '.amr'
};

// 常见音频文件扩展名
const AUDIO_EXTENSIONS = ['.mp3', '.wav', '.m4a', '.aac', '.ogg', '.flac', '.webm', '.3gp', '.amr'];

/**
 * 从 Content-Type 获取文件扩展名
 */
export function getExtensionFromMimeType(mimeType: string): string {
  const cleanMimeType = mimeType.split(';')[0].trim().toLowerCase();
  return AUDIO_MIME_TO_EXT[cleanMimeType] || '';
}

/**
 * 从 URL 路径提取文件扩展名
 */
export function getExtensionFromUrl(url: string): string {
  try {
    const urlPath = new URL(url).pathname;
    const parts = urlPath.split('.');
    if (parts.length > 1) {
      const extension = parts.pop()?.toLowerCase();
      if (extension && extension.length <= 4) {
        return `.${extension}`;
      }
    }
  } catch (error) {
    console.warn('URL 解析失败：', error);
  }
  return '';
}

/**
 * 检查文件名是否已有扩展名
 */
export function hasValidExtension(filename: string): boolean {
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1) return false;

  const extension = filename.substring(lastDotIndex).toLowerCase();
  return AUDIO_EXTENSIONS.includes(extension);
}

/**
 * 确保文件名有合适的扩展名
 * @param filename 原始文件名
 * @param mimeType 可选的 MIME 类型
 * @param url 可选的下载 URL
 * @param defaultExt 默认扩展名，默认为.mp3
 */
export function ensureAudioExtension(
  filename: string,
  mimeType?: string,
  url?: string,
  defaultExt: string = '.mp3'
): string {
  // 如果已经有有效的音频扩展名，直接返回
  if (hasValidExtension(filename)) {
    return filename;
  }

  let extension = '';

  // 1. 优先从 MIME 类型获取扩展名
  if (mimeType) {
    extension = getExtensionFromMimeType(mimeType);
  }

  // 2. 如果没有，从 URL 获取扩展名
  if (!extension && url) {
    extension = getExtensionFromUrl(url);
  }

  // 3. 如果都没有，使用默认扩展名
  if (!extension) {
    extension = defaultExt;
  }

  return filename + extension;
}

/**
 * 从 HTTP 响应头解析文件名
 */
export function parseFilenameFromHeaders(headers: Headers): string | null {
  const contentDisposition = headers.get('Content-Disposition');
  if (!contentDisposition) return null;

  // 尝试多种格式的文件名解析
  const patterns = [
    /filename\*=UTF-8''([^;]+)/i,  // RFC 5987 格式
    /filename\*=([^;]+)/i,         // 带编码的格式
    /filename="([^"]+)"/i,         // 带引号的格式
    /filename=([^;]+)/i            // 不带引号的格式
  ];

  for (const pattern of patterns) {
    const match = contentDisposition.match(pattern);
    if (match && match[1]) {
      try {
        // 尝试 URL 解码
        const decoded = decodeURIComponent(match[1].trim());
        if (decoded) return decoded;
      } catch (error) {
        // 如果解码失败，直接使用原始值
        return match[1].trim();
      }
    }
  }

  return null;
}

/**
 * 清理文件名，移除不安全的字符
 */
export function sanitizeFilename(filename: string): string {
  // 只保留安全的字符：字母、数字、中文、常见标点符号
  return filename
    .replace(/[^\w\u4e00-\u9fff\u3400-\u4dbf\s\-_.()[\]]/g, '_')  // 保留安全字符
    .replace(/\s+/g, '_')                    // 替换空格为下划线
    .replace(/_{2,}/g, '_')                  // 合并多个下划线
    .replace(/^_+|_+$/g, '')                 // 移除开头和结尾的下划线
    .substring(0, 200);                      // 限制长度
}

/**
 * 智能构造下载文件名
 * @param originalName 原始文件名
 * @param response HTTP 响应对象
 * @param suffix 可选的后缀（如"_音频"）
 */
export function buildDownloadFilename(
  originalName: string,
  response: Response,
  suffix?: string
): string {
  // 1. 尝试从响应头获取文件名
  const headerFilename = parseFilenameFromHeaders(response.headers);
  console.log('headerFilename', headerFilename);
  if (headerFilename && hasValidExtension(headerFilename)) {
    const formatFilename = headerFilename;
    return sanitizeFilename(formatFilename);
  }

  // 2. 构造文件名
  let filename = originalName;
  if (suffix) {
    filename += suffix;
    console.log('filename', filename);
  }

  // 3. 确保有扩展名
  const mimeType = response.headers.get('Content-Type');
  const finalFilename = ensureAudioExtension(filename, mimeType || undefined, response.url);

  return sanitizeFilename(finalFilename);
}

export default {
  getExtensionFromMimeType,
  getExtensionFromUrl,
  hasValidExtension,
  ensureAudioExtension,
  parseFilenameFromHeaders,
  sanitizeFilename,
  buildDownloadFilename
};
